<template>
  <EditDialog
    ref="editDialog"
    mode="add"
    :event="newEvent"
    :errors="errors"
    :loading="loading"
    :prefill-date="prefillDate"
    :business-hours="businessHours"
    :confirm-cancel="onCancelDialog"
    :enable-peeking="enablePeeking"
    @save="finish"
    @hide="reset"
    @update-reference="newEvent.reference_changed = true"
  />
</template>

<script>
import EditDialog from './partials/edit/Dialog'

import staffMixin from './partials/mixins/staffMixin'
import fillClientDetailsMixin from './partials/mixins/fillClientDetailsMixin'
const { DateTime: luxon } = require('luxon')
import { store } from 'src/store'

import { addDefaultUnscheduledReminders } from 'components/app/calendar/partials/mixins/remindersMixin'
import awardNewbie from 'src/helpers/awardNewbie'

function getParsedDatesForEvent (start = null, end = null) {
  if (!start) {
    start = luxon.now().plus({ hours: 1 })
  }
  else {
    start = luxon.fromSQL(start)
  }

  if (!end) {
    end = start.plus({ hours: 2 })
  }
  else {
    end = luxon.fromSQL(end)
  }

  return {
    start: start.toSimpleSQL(),
    end: end.toSimpleSQL()
  }
}

function newEvent (startDate = null, endDate = null, allDay = false, timezone = process.env.SYSTEM_TIMEZONE) {
  const { start, end } = getParsedDatesForEvent(startDate, endDate)
  return {
    attendees: [],
    clients: [],
    types: [],
    client_reminders: [],
    staff_reminders: [],
    unscheduled_reminders: [],
    title: null,
    description: null,
    can_edit: true,
    start_date: start,
    end_date: end,
    address: null,
    reference_changed: false,
    reference_id: null,
    status: 'SCHEDULED',
    all_day: allDay,
    color: store.getters['company/getCompany'].appointments_default_color,
    recursion_rule: null,
    note: null,
    recurring_status: 'UNSCHEDULED',
    quotation_id: null,
    timezone
  }
}

export default {
  components: {
    EditDialog
  },
  mixins: [fillClientDetailsMixin, staffMixin],
  props: {
    modelPayload: {
      type: Boolean,
      default: false
    },
    enablePeeking: {
      type: Boolean,
      default: false
    }
  },
  emits: ['created'],
  data () {
    return {
      newEvent: newEvent(),
      errors: {},
      loading: false,
      prefillDate: true,
      businessHours: null
    }
  },
  methods: {
    async open (startDate = null, endDate = null, clients = [], staff = [], allDay = false) {
      const alreadyOpen = !this.$refs.editDialog.open()

      this.prefillDate = false
      if (!!startDate && !!endDate) {
        const timeBetween = Math.abs(this.luxon.fromSQL(endDate).diff(this.luxon.fromSQL(startDate), 'minutes').toObject().minutes)
        this.prefillDate = timeBetween <= 30
      }
      else if (!endDate) {
        this.prefillDate = true
      }

      // If the dialog is already open and the user is using peek mode, they can select another
      //  timeslot to use for this existing new event they're creating
      if (alreadyOpen) {
        // If already open but didn't select a timeslot, don't do any action
        if (!(!!startDate && !!endDate)) {
          return
        }
        const { start, end } = getParsedDatesForEvent(startDate, endDate)
        this.newEvent.start_date = start
        this.newEvent.end_date = end
        this.newEvent.all_day = allDay

        // If staff were selected (staff schedule), add them to the appointment
        if (staff.length > 0) {
          this.baseRemoveAllExistingStaff(this.newEvent)
          for (const ele of staff) {
            this.baseAddStaff(this.newEvent, ele)
          }
        }
        return
      }

      this.errors = {}
      this.newEvent = newEvent(startDate, endDate, allDay, this.company.timezone)
      await this.loadAppointmentDefaults()

      /* If clients were passed directly in */
      if (!this.$route.query.client_id && clients.length > 0) {
        for (const ele of clients) {
          this.baseAddClient(this.newEvent, ele)
        }
      }

      if (staff.length > 0) {
        for (const ele of staff) {
          this.baseAddStaff(this.newEvent, ele)
        }
      }

      // Add default unscheduled reminder
      addDefaultUnscheduledReminders(this.newEvent)
    },
    reset () {
      this.newEvent = newEvent()
    },
    async finish () {
      const event = this.newEvent
      this.errors = {}
      this.loading = true
      const { status, payload, errors } = await this.$post('v1/appointments', {
        model_payload: this.modelPayload,
        reference_changed: event.reference_changed,
        reference_id: event.reference_id,
        start_date: this.luxon.fromSQL(event.start_date).toSimpleSQL(),
        end_date: this.luxon.fromSQL(event.end_date).toSimpleSQL(),
        timezone: event.timezone,
        title: event.title,
        description: event.description,
        address: event.address,
        attendees: event.attendees.map(item => item.id),
        clients: event.clients.map(item => item.id),
        all_day: event.all_day,
        color: event.color,
        status: event.status,
        types: event.types.map(item => item.id),
        recursion_rule: event.recursion_rule ? event.recursion_rule.value : null,
        reminders: [...event.client_reminders, ...event.staff_reminders, ...event.unscheduled_reminders],
        note: event.note,
        recurring_status: event.recurring_status,
        quotation_id: event.quotation_id
      })
      this.loading = false
      if (status === 'success') {
        await awardNewbie(this.newbies.setupCreateAppointment, 'user')

        // If route query params exist, remove them to avoid re-creating the appointment with same params again
        if (this.$route.path.includes('scheduler') && Object.keys(this.$route.query).length > 0) {
          this.$router.push({
            path: this.$route.path
          })
        }
        this.$refs.editDialog.close()
        this.$q.notify({
          color: 'green',
          message: 'Appointment successfully created'
        })
        this.$emit('created', payload)
      }
      else if (status === 'unsubscribed') {
        this.$unsubscribed({
          hitLimit: true,
          modelName: 'appointment'
        })
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    },
    async loadAppointmentDefaults () {
      const { status, payload } = await this.$get('v1/appointments/defaults/values', {
        quotation_id: this.$route.query.quotation_id,
        client_id: this.$route.query.client_id
      })
      if (status === 'success') {
        this.newEvent.reference_id = payload.reference_id
        this.newEvent.quotation_id = payload.quotation_id
        this.businessHours = payload.business_hours
        if (payload.address) {
          this.baseFillAddress(this.newEvent, payload.address)
        }
        if (payload.client) {
          this.baseAddClient(this.newEvent, payload.client)
        }
        if (payload.user) {
          this.baseAddStaff(this.newEvent, payload.user)
        }
        if (this.$route.query.start_date && this.$route.query.end_date) {
          this.newEvent.start_date = this.luxon.fromSQL(this.$route.query.start_date).toSimpleSQL()
          this.newEvent.end_date = this.luxon.fromSQL(this.$route.query.end_date).toSimpleSQL()
        }
      }
    },
    onCancelDialog (func) {
      this.$q.dialog({
        title: 'Confirm',
        message: [
          'Are you sure you want to back?',
          'This appointment will not be saved and any changes will be lost.'
        ],
        ok: true,
        cancel: true,
        color: 'orange-7'
      })
        .onOk(func)
    }
  }
}
</script>
